import { useEffect, useMemo, useState } from 'react';
import DateField from '../components/DateField.jsx';
import { Link } from 'react-router-dom';
import { api, withQuery, money, quantity, date, isoDate, monthLabel, PAYMENT_LABEL } from '../api.js';

const TABS = [
  { key: 'turnover', label: 'Оборот по месеци' },
  { key: 'top', label: 'Най-продавани' },
  { key: 'receivables', label: 'Вземания' },
  { key: 'stock', label: 'Наличности' },
  { key: 'mismatch', label: 'Склад ≠ партиди' },
  { key: 'commissions', label: 'Комисионни' },
  { key: 'cashCategories', label: 'Каса по пера' },
  { key: 'poli', label: 'Справка Поли' },
  { key: 'vat', label: 'Дневници ДДС' },
  { key: 'nap', label: 'Данни за НАП' },
];

export default function Reports() {
  const [tab, setTab] = useState('turnover');
  return (
    <>
      <div className="page-head">
        <h1>Справки</h1>
        <div className="spacer" />
        {TABS.map((t) => (
          <button
            key={t.key}
            className={tab === t.key ? 'primary' : ''}
            onClick={() => setTab(t.key)}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'turnover' && <Turnover />}
      {tab === 'top' && <TopProducts />}
      {tab === 'receivables' && <Receivables />}
      {tab === 'stock' && <StockReport />}
      {tab === 'mismatch' && <BatchMismatch />}
      {tab === 'commissions' && <Commissions />}
      {tab === 'cashCategories' && <CashCategories />}
      {tab === 'poli' && <Poli />}
      {tab === 'vat' && <VatJournal />}
      {tab === 'nap' && <NapExport />}
    </>
  );
}

function useReport(path, params, deps) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(withQuery(path, params))
      .then(setData)
      .catch((e) => setError(e.message));
  }, deps);
  return [data, error];
}

const TURNOVER_METHODS = ['cash', 'bank', 'card', 'postal'];
const TURNOVER_LABEL = { ...PAYMENT_LABEL, postal: 'ППП' };

function Turnover() {
  const [from, setFrom] = useState('2025-01-01');
  const [data, error] = useReport('/api/reports/turnover', { from, docType: 'sale' }, [from]);

  if (error) return <div className="error">{error}</div>;

  const max = data ? Math.max(...data.map((r) => Number(r.total_eur)), 1) : 1;

  return (
    <div className="card">
      <div className="card-head">
        Оборот по месеци
        <div style={{ flex: 1 }} />
        <DateField value={from} onChange={(v) => setFrom(v)} />
      </div>
      {!data ? <div className="empty">Зареждане…</div> : (
        <table className="compact">
          <thead>
            <tr>
              <th>Месец</th><th className="num">Документи</th>
              <th className="num">Без ДДС</th><th className="num">С ДДС</th>
              {TURNOVER_METHODS.map((m) => (
                <th className="num" key={m}>{TURNOVER_LABEL[m]}</th>
              ))}
              <th style={{ width: '20%' }} />
            </tr>
          </thead>
          <tbody>
            {data.map((r) => (
              <tr key={r.period}>
                <td>{monthLabel(r.period)}</td>
                <td className="num muted">{r.documents}</td>
                <td className="num">{money(r.net_eur)}</td>
                <td className="num">{money(r.total_eur)}</td>
                {TURNOVER_METHODS.map((m) => (
                  <td className="num muted" key={m}>{money(r[`${m}_eur`])}</td>
                ))}
                <td>
                  <div style={{
                    height: 8, borderRadius: 4, background: 'var(--accent)',
                    width: `${(Number(r.total_eur) / max) * 100}%`, minWidth: 2,
                  }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TopProducts() {
  const [from, setFrom] = useState('2026-01-01');
  const [data, error] = useReport('/api/reports/top-products', { from, limit: 30 }, [from]);
  if (error) return <div className="error">{error}</div>;
  return (
    <div className="card">
      <div className="card-head">
        Най-продавани стоки
        <div style={{ flex: 1 }} />
        <DateField value={from} onChange={(v) => setFrom(v)} />
      </div>
      {!data ? <div className="empty">Зареждане…</div> : (
        <table className="compact">
          <thead>
            <tr><th>Стока</th><th>Група</th><th className="num">Количество</th><th className="num">Оборот</th></tr>
          </thead>
          <tbody>
            {data.map((r) => (
              <tr key={r.id}>
                <td><Link to={`/products/${r.id}`}>{r.name}</Link></td>
                <td className="muted">{r.group_name}</td>
                <td className="num">{quantity(r.quantity)}</td>
                <td className="num">{money(r.revenue_eur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Receivables() {
  const [data, error] = useReport('/api/reports/receivables', {}, []);
  if (error) return <div className="error">{error}</div>;
  return (
    <div className="card">
      <div className="card-head">
        Вземания от клиенти
        <div style={{ flex: 1 }} />
        {data && <strong>{money(data.totalDue)}</strong>}
      </div>
      {!data ? <div className="empty">Зареждане…</div> : (
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Клиент</th><th>Телефон</th>
                <th className="num">Начално салдо</th>
                <th className="num">Отворени документи</th>
                <th className="num">Дължи</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="muted">{r.phone ?? '—'}</td>
                  <td className="num muted">{money(r.opening_balance)}</td>
                  <td className="num muted">{r.open_documents}</td>
                  <td className="num"><strong>{money(r.due_eur)}</strong></td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={5} className="empty">Няма задължения</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Разминаване между наличността по склад и сбора на партидите.
 *
 * Наследено от старата програма — там двете се водят поотделно.
 * Програмата не ги изравнява сама: коя от двете цифри е вярната се
 * решава с броене на рафта, не по сметка. Затова тук е списък за
 * проверка, а поправката става с ревизия.
 */
/**
 * Парите по пера: за какво излизат от касата.
 *
 * Продажбите и доставките си имат свои справки. Тук е онова, което минава
 * през касата извън търговията — аванси, бензин, СОТ, счетоводство, вода
 * за офиса. В старата програма това стоеше само като код по записа и
 * никой не го е сумирал.
 *
 * Служебното въвеждане и изваждане стои отделно, най-отдолу: то не е
 * харчене, а вечерното изпразване на чекмеджето и сутрешното му
 * зареждане. Смесено с разходите, би затрупало всичко останало — само за
 * последната година то е стотици хиляди.
 */
function CashCategories() {
  const [range, setRange] = useState(() => {
    const dnes = new Date();
    const predi = new Date(dnes.getFullYear(), 0, 1);
    return { from: isoDate(predi), to: isoDate(dnes) };
  });
  /*
   * Избраното перо пътува със заявката, а не се пресява в екрана: за него
   * се искат самите движения, които в общата справка ги няма. Празно
   * означава „всички" — тогава сървърът не връща движения изобщо.
   */
  const [pero, setPero] = useState('');
  const zaqvka = useMemo(
    () => (pero ? { ...range, categoryId: Number(pero) } : range),
    [range, pero]);
  const [data, error] = useReport('/api/reports/cash-categories', zaqvka, [zaqvka]);
  const [vsichkiPera, setVsichkiPera] = useState([]);

  /*
   * Списъкът за падащото меню се пълни от първото зареждане и повече не
   * се пипа. Иначе, щом се избере перо, справката се свива до един ред и
   * менюто ще остане с един избор — този, който вече е направен.
   */
  useEffect(() => {
    if (data?.rows && !pero) setVsichkiPera(data.rows);
  }, [data, pero]);

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="card"><div className="empty">Зареждане…</div></div>;

  const sluzhebni = new Set(['01', '02', '900', '110']);
  const razhodi = data.rows.filter((r) => !sluzhebni.has(String(r.code)));
  const dvizhenia = data.rows.filter((r) => sluzhebni.has(String(r.code)));

  const tablica = (redove, zaglavie) => (
    <div className="card">
      <div className="card-head">{zaglavie}</div>
      <table className="compact">
        <thead>
          <tr>
            <th style={{ width: 60 }}>Код</th>
            <th>Перо</th>
            <th className="num">Движения</th>
            <th className="num">Влезли</th>
            <th className="num">Излезли</th>
            <th>Период</th>
          </tr>
        </thead>
        <tbody>
          {redove.map((r) => (
            <tr
              key={r.id}
              onClick={() => setPero(String(r.id) === pero ? '' : String(r.id))}
              className={String(r.id) === pero ? 'selected' : ''}
              style={{ cursor: 'pointer' }}
            >
              <td className="muted">{r.code}</td>
              <td>{r.name}</td>
              <td className="num muted">{r.movements}</td>
              <td className="num" style={{ color: 'var(--ok)' }}>
                {Number(r.total_in) ? <strong>{money(r.total_in)}</strong> : ''}
              </td>
              <td className="num" style={{ color: 'var(--danger)' }}>
                {Number(r.total_out) ? <strong>{money(r.total_out)}</strong> : ''}
              </td>
              <td className="muted">
                {date(r.first_date)} – {date(r.last_date)}
              </td>
            </tr>
          ))}
          {redove.length === 0 && (
            <tr><td colSpan={6} className="empty">Няма движения за периода</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div className="toolbar">
        <label className="field">
          От
          <DateField value={range.from} onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
        </label>
        <label className="field">
          До
          <DateField value={range.to} onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
        </label>
        <label className="field">
          Вид разход
          <select value={pero} onChange={(e) => setPero(e.target.value)}>
            <option value="">— всички —</option>
            {vsichkiPera.map((r) => (
              <option key={r.id} value={r.id}>{r.code} · {r.name}</option>
            ))}
          </select>
        </label>
        {pero && (
          <button type="button" className="btn ghost" onClick={() => setPero('')}>
            Покажи всички
          </button>
        )}
        <div className="spacer" />
        <span className="badge danger">похарчено {money(data.totals.spent)}</span>
        {Number(data.totals.received) > 0 && (
          <span className="badge ok">влезли {money(data.totals.received)}</span>
        )}
      </div>

      {tablica(razhodi, 'Разходи и приходи по пера')}
      {tablica(dvizhenia, 'Служебни движения и преводи — не са харчене')}

      {data.movements && (
        <div className="card">
          <div className="card-head">
            Движения по перото
            {data.movements.length >= 500 && (
              <span className="hint"> · показани са последните 500</span>
            )}
          </div>
          <table className="compact">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Дата</th>
                <th>Основание</th>
                <th className="num" style={{ width: 120 }}>Влезли</th>
                <th className="num" style={{ width: 120 }}>Излезли</th>
                <th style={{ width: 150 }}>Каса</th>
              </tr>
            </thead>
            <tbody>
              {data.movements.map((m) => (
                <tr key={m.id}>
                  <td className="muted">{date(m.movement_date)}</td>
                  <td>
                    {m.reason}
                    {m.note && <div className="muted small">{m.note}</div>}
                  </td>
                  <td className="num" style={{ color: 'var(--ok)' }}>
                    {m.direction === 'in' ? <strong>{money(m.amount)}</strong> : ''}
                  </td>
                  <td className="num" style={{ color: 'var(--danger)' }}>
                    {m.direction === 'out' ? <strong>{money(m.amount)}</strong> : ''}
                  </td>
                  <td className="muted">{m.register_name}</td>
                </tr>
              ))}
              {data.movements.length === 0 && (
                <tr><td colSpan={5} className="empty">Няма движения за периода</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {Number(data.withoutCategory?.movements) > 0 && (
        <p className="hint">
          {data.withoutCategory.movements} движения са без перо и не влизат в горните
          сборове.
        </p>
      )}
    </>
  );
}

/**
 * Справка Поли — месечният стокооборот за счетоводството.
 *
 * Досега се вадеше от старата програма и се пращаше по пощата. Тук е
 * същата, със същите тринайсет колони и същите думи, за да не се налага
 * счетоводството да свиква с нов вид.
 *
 * Периодът по подразбиране е миналият месец: справката се прави след
 * неговото приключване, не за текущия, който още се пълни.
 */
function Poli() {
  const [range, setRange] = useState(() => {
    const dnes = new Date();
    const prv = new Date(Date.UTC(dnes.getFullYear(), dnes.getMonth() - 1, 1));
    const posl = new Date(Date.UTC(dnes.getFullYear(), dnes.getMonth(), 0));
    return { from: prv.toISOString().slice(0, 10), to: posl.toISOString().slice(0, 10) };
  });
  const [data, error] = useReport('/api/reports/poli', range, [range]);

  // Адресът на счетоводството се пази в настройките и се вижда тук, за да
  // може да се смени, без да се търси из менютата.
  const [kam, setKam] = useState(null);
  const [redakcia, setRedakcia] = useState(false);
  const [nov, setNov] = useState('');
  const [prashta, setPrashta] = useState(false);
  const [vest, setVest] = useState(null);
  const [greshka, setGreshka] = useState(null);

  useEffect(() => {
    api.get('/api/reports/poli/recipient')
      .then((r) => setKam(r.email))
      .catch((e) => setGreshka(e.message));
  }, []);

  async function zapishiAdres() {
    try {
      const r = await api.put('/api/reports/poli/recipient', { email: nov.trim() });
      setKam(r.email);
      setRedakcia(false);
      setVest(`Адресът е сменен на ${r.email}`);
    } catch (e) { setGreshka(e.message); }
  }

  /*
   * Цветът на реда идва готов от бекенда (`color_grupa`) — сметнат
   * веднъж там, за да съвпада точно с оцветяването в изтегления Excel.
   * Палитрата тук трябва да е БУКВАЛНО същата като първите 6 цвята в
   * `ROW_FILLS` (server/src/services/xlsx.js).
   */
  const PAIR_PALETTE = ['#dbe9ff', '#ffe0ef', '#dff5e3', '#fdead0', '#ece2ff', '#d8f6f4'];

  function redStil(r) {
    if (!r.color_grupa) return undefined;
    return { backgroundColor: PAIR_PALETTE[r.color_grupa.idx % PAIR_PALETTE.length] };
  }

  async function izprati() {
    if (!window.confirm(`Изпращане на справката до ${kam}?`)) return;
    setPrashta(true);
    setGreshka(null);
    try {
      const r = await api.post('/api/reports/poli/send', range);
      setVest(r.redirected
        ? `Пробен режим: писмото отиде до ${r.to}, а не до счетоводството.`
        : `Изпратено до ${r.to} — ${r.rows} документа.`);
    } catch (e) { setGreshka(e.message); }
    setPrashta(false);
  }

  // Месецът се сменя с една стъпка назад или напред, вместо с две дати.
  const mesec = (nasoka) => setRange((r) => {
    const d = new Date(`${r.from}T00:00:00Z`);
    const prv = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + nasoka, 1));
    const posl = new Date(Date.UTC(prv.getUTCFullYear(), prv.getUTCMonth() + 1, 0));
    return { from: prv.toISOString().slice(0, 10), to: posl.toISOString().slice(0, 10) };
  });

  /*
   * Половинки месец.
   *
   * Понякога справката се дава два пъти — до 15-о и от 16-о. Иначе двете
   * дати се набират на ръка всеки път, а краят на месеца се сеща различно
   * според месеца.
   */
  const polovina = (koya) => setRange((r) => {
    const d = new Date(`${r.from}T00:00:00Z`);
    const g = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const kraj = new Date(Date.UTC(g, m + 1, 0)).toISOString().slice(0, 10);
    const dva = (n) => String(n).padStart(2, '0');
    return koya === 1
      ? { from: `${g}-${dva(m + 1)}-01`, to: `${g}-${dva(m + 1)}-15` }
      : { from: `${g}-${dva(m + 1)}-16`, to: kraj };
  });

  const celiyat = `${range.from.slice(0, 8)}01` === range.from
    && range.to === new Date(Date.UTC(
      Number(range.from.slice(0, 4)), Number(range.from.slice(5, 7)), 0)).toISOString().slice(0, 10);
  const parvata = range.from.endsWith('-01') && range.to.endsWith('-15');
  const vtorata = range.from.endsWith('-16') && !parvata && !celiyat;

  if (error) return <div className="error">{error}</div>;
  if (!data) return <div className="card"><div className="empty">Зареждане…</div></div>;

  const imeNaMeseca = new Date(`${range.from}T00:00:00Z`)
    .toLocaleDateString('bg-BG', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <>
      <div className="toolbar">
        <button type="button" onClick={() => mesec(-1)} title="Предишен месец">‹</button>
        <strong style={{ minWidth: 150, textAlign: 'center' }}>{imeNaMeseca}</strong>
        <button type="button" onClick={() => mesec(1)} title="Следващ месец">›</button>
        <button type="button" className={celiyat ? 'primary' : ''}
          onClick={() => mesec(0)}>цял месец</button>
        <button type="button" className={parvata ? 'primary' : ''}
          onClick={() => polovina(1)}>1–15</button>
        <button type="button" className={vtorata ? 'primary' : ''}
          onClick={() => polovina(2)}>16–края</button>
        <label className="field">
          От
          <DateField value={range.from} onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
        </label>
        <label className="field">
          До
          <DateField value={range.to} onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
        </label>
        <div className="spacer" />
        <span className="badge">{data.rows.length} реда</span>
        <span className="badge">основа {money(data.totals.osnova)}</span>
        <span className="badge">ДДС {money(data.totals.dds)}</span>
        <a className="btn"
          href={withQuery('/api/reports/poli', { ...range, format: 'xlsx' })}>
          Изтегли
        </a>
        <button type="button" className="primary" disabled={prashta || !kam}
          onClick={izprati}>
          {prashta ? 'Изпращам…' : 'Изпрати на счетоводството'}
        </button>
      </div>

      <div className="toolbar">
        {redakcia ? (
          <>
            <label className="field" style={{ flex: 1, minWidth: 260 }}>
              Имейл на счетоводството
              <input value={nov} autoFocus
                onChange={(e) => setNov(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') zapishiAdres();
                  if (e.key === 'Escape') setRedakcia(false);
                }} />
            </label>
            <button type="button" className="primary" onClick={zapishiAdres}>Запиши</button>
            <button type="button" onClick={() => setRedakcia(false)}>Откажи</button>
          </>
        ) : (
          <>
            <span className="muted">Изпраща се до <strong>{kam ?? '…'}</strong></span>
            <button type="button" onClick={() => { setNov(kam ?? ''); setRedakcia(true); }}>
              Смени адреса
            </button>
          </>
        )}
      </div>

      {vest && <div className="notice">{vest}</div>}
      {greshka && <div className="error">{greshka}</div>}

      <div className="card">
        <table className="compact">
          <thead>
            <tr>
              <th style={{ width: 100 }}>От дата</th>
              <th>Тип</th>
              <th>Плащане</th>
              <th style={{ width: 110 }}>Документ</th>
              <th style={{ width: 110 }}>Фактура</th>
              <th style={{ width: 110 }}>Анулира</th>
              <th>Фирма</th>
              <th style={{ width: 90 }}>Рег. по ДДС</th>
              <th style={{ width: 110 }}>ЕИК</th>
              <th className="num">Без ДДС</th>
              <th className="num">ДДС</th>
              <th className="num">Стойност с ДДС</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={`${r.dokument}-${i}`} style={redStil(r)}>
                <td className="muted">{r.ot_data}</td>
                <td>{r.tip}</td>
                <td className="muted">{r.plashtane}</td>
                <td>{r.dokument}</td>
                {/* Празно за обикновен касов бон — попълва се само когато
                    протоколът е част от обединена фактура. */}
                <td className="muted">{r.fak_no}</td>
                {/* Насрещният документ на анулирана двойка — намерен по
                    контрагент + обратна сума, не по изрична връзка. */}
                <td className="muted">{r.svarzan}</td>
                <td>{r.firma}</td>
                <td className="muted">{r.dds_reg}</td>
                {/* Без ЕИК счетоводството не може да осчетоводи фактурата. */}
                <td className={r.eik ? 'muted' : 'error'}>{r.eik || 'липсва'}</td>
                <td className="num"><strong>{money(r.bez_dds)}</strong></td>
                <td className="num">{money(r.stoinost_dds)}</td>
                <td className="num">{money(r.stoinost_s_dds)}</td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={12} className="empty">Няма продажби за периода</td></tr>
            )}
          </tbody>
          {data.rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={9}><strong>{data.rows.length} ред(а)</strong></td>
                <td className="num"><strong>{money(data.totals.osnova)}</strong></td>
                <td className="num"><strong>{money(data.totals.dds)}</strong></td>
                <td className="num"><strong>{money(data.totals.sDds)}</strong></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

/**
 * Изнасяне на данните по Приложение № 29, т. 18.
 *
 * Наредбата иска деветте таблици да са достъпни през интерфейса и да се
 * изнасят в XLS/XLSX или CSV, с филтри по период, търговски обект, ФУ,
 * работно място и оператор. Тук те излизат като листове в една книга.
 *
 * Екранът е нарочно прост: това не е справка за всекидневна работа, а
 * онова, което се подава при проверка.
 */
/**
 * Дневници по ДДС — продажби и покупки за месеца.
 *
 * Продажбите излизат от същите данни като справката на Поли, затова
 * сборът им съвпада с нея до стотинка. Доставките без номер на фактура
 * не влизат в дневника и се показват отдолу, за да се допълнят.
 */
function VatJournal() {
  const [range, setRange] = useState(() => {
    const dnes = new Date();
    const prv = new Date(Date.UTC(dnes.getFullYear(), dnes.getMonth() - 1, 1));
    const posl = new Date(Date.UTC(dnes.getFullYear(), dnes.getMonth(), 0));
    return { from: prv.toISOString().slice(0, 10), to: posl.toISOString().slice(0, 10) };
  });
  const [koy, setKoy] = useState('prodazhbi');
  const [data, error] = useReport('/api/reports/vat-journal', range, [range]);

  const mesec = (nasoka) => setRange((r) => {
    const d = new Date(`${r.from}T00:00:00Z`);
    const prv = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + nasoka, 1));
    const posl = new Date(Date.UTC(prv.getUTCFullYear(), prv.getUTCMonth() + 1, 0));
    return { from: prv.toISOString().slice(0, 10), to: posl.toISOString().slice(0, 10) };
  });
  const imeNaMeseca = new Date(`${range.from}T00:00:00Z`)
    .toLocaleDateString('bg-BG', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  if (error) return <div className="error">{error}</div>;

  const prod = data?.prodazhbi;
  const pok = data?.pokupki;

  return (
    <>
      <div className="toolbar">
        <button type="button" onClick={() => mesec(-1)} title="Предишен месец">‹</button>
        <strong style={{ minWidth: 150, textAlign: 'center' }}>{imeNaMeseca}</strong>
        <button type="button" onClick={() => mesec(1)} title="Следващ месец">›</button>
        <label className="field">
          От
          <DateField value={range.from} onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
        </label>
        <label className="field">
          До
          <DateField value={range.to} onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
        </label>
        <div className="spacer" />
        <a className="btn primary"
          href={withQuery('/api/reports/vat-journal', { ...range, format: 'xlsx' })}>
          Изтегли двата дневника
        </a>
      </div>

      <div className="toolbar">
        <button type="button" className={koy === 'prodazhbi' ? 'primary' : ''}
          onClick={() => setKoy('prodazhbi')}>
          Дневник на продажбите{prod ? ` (${prod.rows.length})` : ''}
        </button>
        <button type="button" className={koy === 'pokupki' ? 'primary' : ''}
          onClick={() => setKoy('pokupki')}>
          Дневник на покупките{pok ? ` (${pok.rows.length})` : ''}
        </button>
        <div className="spacer" />
        {data && <span className="muted">{data.firma.ime} · {data.firma.in}</span>}
      </div>

      {!data && <div className="card"><div className="empty">Зареждане…</div></div>}

      {data && koy === 'prodazhbi' && (
        <div className="card">
          <table className="compact">
            <thead>
              <tr>
                <th style={{ width: 50 }}>№</th>
                <th>Вид</th>
                <th>Номер</th>
                <th style={{ width: 95 }}>Дата</th>
                <th>ИН на контрагента</th>
                <th>Контрагент</th>
                <th className="num">Данъчна основа</th>
                <th className="num">ДДС</th>
                <th className="num">ДО 20%</th>
                <th className="num">ДДС 20%</th>
                <th className="num">ДО без ДДС</th>
              </tr>
            </thead>
            <tbody>
              {prod.rows.map((r) => (
                <tr key={r.nomer_red}>
                  <td className="muted">{r.nomer_red}</td>
                  <td>{r.vid}</td>
                  <td>{r.nomer}</td>
                  <td>{r.data}</td>
                  <td>{r.in}</td>
                  <td>{r.ime}</td>
                  <td className="num">{money(r.osnova_obshto)}</td>
                  <td className="num">{money(r.dds_obshto)}</td>
                  <td className="num">{money(r.osnova20)}</td>
                  <td className="num">{money(r.dds20)}</td>
                  <td className="num">{r.osnova0 ? money(r.osnova0) : ''}</td>
                </tr>
              ))}
              {prod.rows.length === 0 && (
                <tr><td colSpan={11} className="empty">Няма продажби за периода</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={6}>Общо</th>
                <th className="num">{money(prod.totals.osnova_obshto)}</th>
                <th className="num">{money(prod.totals.dds_obshto)}</th>
                <th className="num">{money(prod.totals.osnova20)}</th>
                <th className="num">{money(prod.totals.dds20)}</th>
                <th className="num">{money(prod.totals.osnova0)}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {data && koy === 'pokupki' && (
        <>
          <div className="card">
            <table className="compact">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>№</th>
                  <th>Вид</th>
                  <th>Номер</th>
                  <th style={{ width: 95 }}>Дата</th>
                  <th>ИН на доставчика</th>
                  <th>Доставчик</th>
                  <th className="num">ДО без право на ДК</th>
                  <th className="num">ДО с пълен ДК</th>
                  <th className="num">ДДС с пълен ДК</th>
                </tr>
              </thead>
              <tbody>
                {pok.rows.map((r) => (
                  <tr key={r.nomer_red}>
                    <td className="muted">{r.nomer_red}</td>
                    <td>{r.vid}</td>
                    <td><Link to={`/documents/${r.id}`}>{r.nomer}</Link></td>
                    <td>{r.data}</td>
                    <td>{r.in}</td>
                    <td>{r.ime}</td>
                    <td className="num">{r.osnova_bez_dk ? money(r.osnova_bez_dk) : ''}</td>
                    <td className="num">{r.osnova_dk ? money(r.osnova_dk) : ''}</td>
                    <td className="num">{money(r.dds_dk)}</td>
                  </tr>
                ))}
                {pok.rows.length === 0 && (
                  <tr><td colSpan={9} className="empty">Няма доставки с фактура за периода</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={6}>Общо</th>
                  <th className="num">{money(pok.totals.osnova_bez_dk)}</th>
                  <th className="num">{money(pok.totals.osnova_dk)}</th>
                  <th className="num">{money(pok.totals.dds_dk)}</th>
                </tr>
              </tfoot>
            </table>
          </div>

          {pok.bezFaktura.length > 0 && (
            <div className="card">
              <div className="notice">
                {pok.bezFaktura.length} доставки нямат номер на фактура и не влизат
                в дневника. Допълни номера в доставката, за да влязат.
              </div>
              <table className="compact">
                <thead>
                  <tr>
                    <th style={{ width: 95 }}>Дата</th>
                    <th>Доставка</th>
                    <th>Доставчик</th>
                    <th className="num">Сума с ДДС</th>
                  </tr>
                </thead>
                <tbody>
                  {pok.bezFaktura.map((r) => (
                    <tr key={r.id}>
                      <td>{r.data}</td>
                      <td><Link to={`/documents/${r.id}`}>{r.dokument}</Link></td>
                      <td>{r.ime}</td>
                      <td className="num">{money(r.suma)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function NapExport() {
  const [range, setRange] = useState(() => {
    const dnes = new Date();
    return {
      from: `${dnes.getFullYear()}-01-01`,
      to: isoDate(dnes),
    };
  });
  const [warehouseId, setWarehouseId] = useState('');
  const [operatorId, setOperatorId] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [operators, setOperators] = useState([]);
  const [preglezh, setPreglezh] = useState(null);
  const [greshka, setGreshka] = useState(null);

  useEffect(() => {
    api.get('/api/warehouses').then(setWarehouses).catch(() => {});
    api.get('/api/reports/operators').then(setOperators).catch(() => {});
  }, []);

  const zaqvka = {
    ...range,
    warehouseId: warehouseId || undefined,
    operatorId: operatorId || undefined,
  };

  async function prebroi() {
    setGreshka(null);
    try {
      setPreglezh(await api.get(withQuery('/api/reports/nap', { ...zaqvka, format: 'json' })));
    } catch (e) { setGreshka(e.message); }
  }

  return (
    <>
      <div className="toolbar">
        <label className="field">
          От
          <DateField value={range.from} onChange={(v) => setRange((r) => ({ ...r, from: v }))} />
        </label>
        <label className="field">
          До
          <DateField value={range.to} onChange={(v) => setRange((r) => ({ ...r, to: v }))} />
        </label>
        <label className="field">
          Търговски обект
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">всички</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </label>
        <label className="field">
          Оператор
          <select value={operatorId} onChange={(e) => setOperatorId(e.target.value)}>
            <option value="">всички</option>
            {operators.map((o) => (
              <option key={o.id} value={o.id}>{o.full_name} ({o.operator_code})</option>
            ))}
          </select>
        </label>
        <div className="spacer" />
        <button type="button" onClick={prebroi}>Преброй</button>
        <a className="btn primary" href={withQuery('/api/reports/nap', { ...zaqvka, format: 'xlsx' })}>
          Изтегли
        </a>
      </div>

      {greshka && <div className="error">{greshka}</div>}

      <div className="card">
        <div className="card-head">Какво съдържа файлът</div>
        <div className="card-body">
          <p className="hint" style={{ marginTop: 0 }}>
            Осемнайсет листа: осемте таблици по т. 18.1–18.8 и десетте
            номенклатури по т. 18.9. Сумите са в евро — наредбата казва
            „в лв.", но от 01.01.2026 националната валута е еврото.
          </p>
          {preglezh && (
            <table className="compact">
              <thead>
                <tr><th>Таблица</th><th className="num">Редове</th></tr>
              </thead>
              <tbody>
                {preglezh.sheets.map((l) => (
                  <tr key={l.name}>
                    <td>{l.name}</td>
                    <td className={`num ${l.rows ? '' : 'muted'}`}>{l.rows}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function BatchMismatch() {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [data, error] = useReport('/api/reports/stock-batch-mismatch',
    { warehouseId }, [warehouseId]);

  useEffect(() => { api.get('/api/warehouses').then(setWarehouses).catch(() => {}); }, []);

  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <div className="card">
        <div className="card-head">
          Разминаване склад ≠ партиди
          <div style={{ flex: 1 }} />
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">всички складове</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>

        {data && (
          <div className="card-body">
            <div className="form-grid">
              <div><div className="k">Стоки</div><div className="v">{data.totals.items}</div></div>
              <div><div className="k">Бройки разлика</div>
                <div className="v">{quantity(data.totals.pieces)}</div></div>
              <div><div className="k">Складът показва повече</div>
                <div className="v">{data.totals.stockHigher}</div></div>
              <div><div className="k">Партидите показват повече</div>
                <div className="v">{data.totals.batchesHigher}</div></div>
            </div>
            <p className="hint" style={{ marginBottom: 0 }}>
              Разликата се поправя с <strong>ревизия</strong>, след като стоката
              бъде преброена. Програмата нарочно не изравнява сама — кое от
              двете числа е вярното не се решава по сметка.
            </p>
          </div>
        )}
      </div>

      <div className="card">
        {!data ? <div className="empty">Зареждане…</div> : data.items.length === 0 ? (
          <div className="empty">Няма разминавания</div>
        ) : (
          <div className="table-wrap">
            <table className="compact">
              <thead>
                <tr>
                  <th>Стока</th><th>Група</th><th>Склад</th>
                  <th className="num">По склад</th>
                  <th className="num">По партиди</th>
                  <th className="num">Разлика</th>
                  <th className="num">Партиди</th>
                  <th className="num">Стойност</th>
                  <th>Последно движение</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r, i) => {
                  const diff = Number(r.diff);
                  return (
                    <tr key={`${r.product_id}-${r.warehouse_id}-${i}`}>
                      <td><Link to={`/products/${r.product_id}`}>{r.name}</Link></td>
                      <td className="muted">{r.group_name}</td>
                      <td className="muted">{r.warehouse_name}</td>
                      <td className="num">{quantity(r.stock_qty)} {r.unit}</td>
                      <td className="num">{quantity(r.batch_qty)} {r.unit}</td>
                      <td className="num">
                        <strong style={{ color: diff > 0 ? 'var(--warn)' : 'var(--danger)' }}>
                          {diff > 0 ? '+' : ''}{quantity(diff)}
                        </strong>
                      </td>
                      <td className="num muted">{r.batch_count}</td>
                      <td className="num">{money(r.diff_value)}</td>
                      <td className="muted">{r.last_move ? date(r.last_move) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function StockReport() {
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [belowMin, setBelowMin] = useState(false);
  const [data, error] = useReport('/api/reports/stock', { warehouseId, belowMin }, [warehouseId, belowMin]);

  useEffect(() => { api.get('/api/warehouses').then(setWarehouses).catch(() => {}); }, []);

  if (error) return <div className="error">{error}</div>;
  return (
    <div className="card">
      <div className="card-head">
        Наличности
        <div style={{ flex: 1 }} />
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 400 }}>
          <input type="checkbox" checked={belowMin} onChange={(e) => setBelowMin(e.target.checked)} />
          само под минимума
        </label>
        <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
          <option value="">всички складове</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        {data && <strong>{money(data.totalValue)}</strong>}
      </div>
      {!data ? <div className="empty">Зареждане…</div> : (
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Стока</th><th>Група</th><th>Склад</th>
                <th className="num">Наличност</th>
                <th className="num">Доставна</th>
                <th className="num">Стойност</th>
              </tr>
            </thead>
            <tbody>
              {data.items.slice(0, 500).map((r, i) => (
                <tr key={`${r.product_id}-${i}`}>
                  <td><Link to={`/products/${r.product_id}`}>{r.name}</Link></td>
                  <td className="muted">{r.group_name}</td>
                  <td className="muted">{r.warehouse_name}</td>
                  <td className="num">{quantity(r.quantity)} {r.unit}</td>
                  <td className="num">{money(r.purchase_price)}</td>
                  <td className="num">{money(r.stock_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.items.length > 500 && (
            <div className="pager">показани са първите 500 от {data.count}</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Комисионни на агентите.
 *
 * Ползва се няколко пъти в годината, при плащането. Затова тръгва от
 * началото на текущата година и показва всички агенти наведнъж — така
 * въпросът „на кого колко дължим" има отговор още при отварянето.
 * Изборът на агент разгъва сметката до клиент и група.
 */
function Commissions() {
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [agentId, setAgentId] = useState('');
  const [agents, setAgents] = useState([]);

  const [data, error] = useReport(
    '/api/reports/agent-commissions',
    { from: from || undefined, to: to || undefined, agentId: agentId || undefined },
    [from, to, agentId]
  );

  useEffect(() => {
    api.get(withQuery('/api/partners', { type: 'agent', limit: 200 }))
      .then((r) => setAgents(r.items))
      .catch(() => setAgents([]));
  }, []);

  // Изнасянето минава по същия адрес със същите условия — файлът е това,
  // което се вижда на екрана, а не втора сметка.
  const exportUrl = withQuery('/api/reports/agent-commissions.xlsx', {
    from: from || undefined, to: to || undefined, agentId: agentId || undefined,
  });

  if (error) return <div className="error">{error}</div>;

  return (
    <div className="card">
      <div className="card-head">
        Комисионни на агентите
        <div style={{ flex: 1 }} />
        <DateField value={from} onChange={setFrom} />
        <DateField value={to} onChange={setTo} />
        <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
          <option value="">всички агенти</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <a className="button" href={exportUrl} download>Excel</a>
        {data && <strong>{money(data.totals.commission)}</strong>}
      </div>

      {!data ? <div className="empty">Зареждане…</div> : data.rows.length === 0 ? (
        <div className="empty">
          За този период няма продажби към клиенти на агенти.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="compact">
            <thead>
              {data.mode === 'agents' ? (
                <tr>
                  <th>Агент</th>
                  <th className="num">Клиенти</th>
                  <th className="num">Оборот без ДДС</th>
                  <th className="num">Комисионна</th>
                </tr>
              ) : (
                <tr>
                  <th>Клиент</th><th>Група</th>
                  <th className="num">Оборот без ДДС</th>
                  <th className="num">Процент</th>
                  <th className="num">Комисионна</th>
                </tr>
              )}
            </thead>
            <tbody>
              {data.mode === 'agents' ? data.rows.map((r) => (
                <tr key={r.agentId}>
                  <td>
                    <button className="link" onClick={() => setAgentId(String(r.agentId))}>
                      {r.agentName}
                    </button>
                  </td>
                  <td className="num muted">{r.clients}</td>
                  <td className="num">{money(r.net)}</td>
                  <td className="num"><strong>{money(r.commission)}</strong></td>
                </tr>
              )) : data.rows.map((r, i) => (
                <tr key={`${r.clientId}-${i}`}>
                  <td><Link to={`/partners/${r.clientId}`}>{r.clientName}</Link></td>
                  <td className="muted">{r.groupPath}</td>
                  <td className="num">{money(r.net)}</td>
                  <td className="num muted">{r.percent}%</td>
                  <td className="num">{money(r.commission)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>Общо</th>
                <th />
                {data.mode === 'agent' && <th />}
                <th className="num">{money(data.totals.net)}</th>
                <th className="num">{money(data.totals.commission)}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
